import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ActionForm } from './ActionForm';
import { getOrgAliases, showForm, addItem, hasChanged, getContracts, searchPackages, getReport, getProducts } from '../../store/formSlice';
import { compareArrays, formatDate, noneToNull } from '../../utils';
import { clone, filter, find, findIndex, flatten, get, has, toLower } from 'lodash';
import { useLocation } from 'react-router';
import { COMMUNITY_ORGS, CONTRACT_STATUS, ECO_RATING, GRANTS, GRANT_STATUS, KITSTYLE, MINOS, ORG_STATUS, PACKAGE_STATUS, REPORT_FORMATS, SWTYPES, SYSARCH, USER_STATUS, YESNO } from '../../settings';
import { registerPackage } from '../../store/uploadSlice';
import { pingServer } from '../../store/userSlice';
import { addOrgLicense, editOrgLicense } from '../../store/api/organisations';
import { updatePackages } from '../../store/api/packages';


const ActionFormContainer = () => {
    const dispatch = useDispatch();
    const entity = useSelector(state => state.form.visible);
    const { values, aliases, loading, components, hasChanges, contracts, packages, products } = useSelector(state => state.form);
    const { details } = useSelector(state => state.data);
    const { isAdmin, orgAlias, defaultOrgName, email } = useSelector(state => state.user.grants);

    let alias, userEmail;
    const parts = useLocation().pathname.split("/"),
    aliasIdx = parts.indexOf("organisations") + 1,
    emailIdx = parts.indexOf("users") + 1;
    if (aliasIdx > 0) alias = parts[aliasIdx];
    if (emailIdx > 0) userEmail = parts[emailIdx];
    const editMode = Object.keys(values).length > 0;

    const initialState = {
        values: {},
        errors: {}
    };

    const [ state, setState ] = useState(initialState);

    useEffect(() => {
        if (entity && Object.keys(formFields).indexOf(entity) > -1) {
            dispatch(pingServer());
            setState({ values: editMode ? values : formFields[entity].initialValues ? formFields[entity].initialValues : {}, errors: {} });
            const interval = setInterval(() => {
                dispatch(pingServer())
            }, 60000)

            return () => {
                setState(initialState);
                clearInterval(interval)
            };
        } else {
            setState(initialState);
        };
    }, [ entity ]);

    useEffect(() => {
        if (isAdmin && (["users", "grants", "contracts", "licenses"].includes(entity)) && !aliases.length) {
            dispatch(getOrgAliases());
        }
        if (entity === "prereq") {
            dispatch(searchPackages(values.sysarch));
        };
        if (entity == "packages" && !products.length) {
            dispatch(getProducts());
        };
        if (["contracts", "restrictions", "packages", "pkg_mass_update"].includes(entity) && !contracts.length) {
            dispatch(getContracts());
        };
    }, [ entity, dispatch, aliases.length ]);

    const countFileSizeLeft = (maxSize) => {
        const { values } = state;
        let sizeUsed = 0;
        if (Array.isArray(values.files) && values.files.length) {
            values.files.map(file => sizeUsed += file.file ? file.file.size : file.size);
        }
        return maxSize - sizeUsed;
    };

    const formFields = {
        organisations: {
            title: editMode ? "Edit Organization Info" : "Add Organization",
            fields: [
                {
                    key: "name",
                    type: "input",
                    label: "Organization Name",
                    rules: [
                        { required: true }
                    ]
                },
                {
                    key: "alias",
                    type: "input",
                    label: "Organization alias",
                    max: 3,
                    disabled: editMode,
                    rules: [
                        { required: true },
                        { pattern: /^[a-z]{3}$/, message: "Alias must contain only 3 lowercase letters." }
                    ]
                },
                {
                    key: "addr",
                    type: "input",
                    label: "Address"
                },
                [
                    {
                        key: "phone",
                        type: "input",
                        label: "Phone",
                        rules: [
                            { pattern: /^[0-9+() -]*$/, message: "Invalid phone number." }
                        ]
                    },
                    {
                        key: "email",
                        type: "input",
                        label: "Contact E-Mail",
                        rules: [
                            { pattern: /^[a-zA-Z0-9._%+-]{1,64}@(?:[a-zA-Z0-9-]{1,63}\.){1,125}[a-zA-Z]{2,63}$/, message: "Invalid e-mail." }
                        ]
                    }
                ],
                {
                    key: "notes",
                    type: "text",
                    label: "notes",
                }
            ]
        },
        users: {
            title: editMode ? "Edit User Info" : "Add User",
            fields: [
                {
                    key: "email",
                    type: "input",
                    label: "E-Mail",
                    max: 60,
                    rules: [
                        { required: true },
                        { pattern: /^[a-zA-Z0-9._%+-]{1,64}@(?:[a-zA-Z0-9-]{1,63}\.){1,125}[a-zA-Z]{2,63}$/, message: "Invalid e-mail." }
                    ],
                    disabled: editMode
                },
                {
                    key: "name",
                    type: "input",
                    label: "Full User Name",
                    max: 60,
                    rules: [
                        { required: true },
                        { pattern: /^[a-z ,.'-]+$/i, message: "User name can contain only Latin letters, spaces, commas, dots, dashes, and apostrophes." }
                    ]
                },
                {
                    key: "orgAlias",
                    type: "select",
                    label: "Default Organization",
                    options: isAdmin
                                ? editMode && Array.isArray(details.grants) ? details.grants.map(grant => ({ key: grant.orgAlias, title: `[${grant.orgAlias}] ${grant.orgName}`})) : aliases
                                : [{ key: orgAlias, title: `[${orgAlias}] ${defaultOrgName}` }],
                    search: true,
                    disabled: alias && !editMode || !isAdmin,
                    loading: loading,
                    rules: [
                        { required: true }
                    ]
                },
                {
                    key: "grant",
                    type: "select",
                    label: "Grant Name",
                    options: isAdmin ? GRANTS : filter(GRANTS, { visible: true }),
                    rules: [
                        { required: true }
                    ],
                    disabled: editMode
                },
            ],
            initialValues: {
                // email: null,
                // name: null,
                orgAlias: isAdmin ? alias : orgAlias,
                grant: "partner",
                // expiry: undefined
            }
        },
        issues: {
            title: "Create Issue",
            fields: [
                {
                    key: "summary",
                    type: "input",
                    label: "Summary",
                    max: 255,
                    rules: [
                        { required: true },
                        { pattern: /^(?!\s*$).+/, message: "The summary can contain at least 1 character and a maximum length of 255 characters." }
                    ]
                },
                {
                    key: "description",
                    type: "text",
                    label: "Description",
                    rows: 5,
                    rules: [
                        { required: true },
                        { pattern: /^(?!\s*$).+/, message: "The description can contain at least 1 character." }
                    ]
                },
                [
                    {
                        key: "product",
                        type: "select",
                        options: components,
                        disabled: COMMUNITY_ORGS.includes(orgAlias),
                        label: "Product",
                        loading: loading,
                        rules: [
                            { required: true }
                        ]
                    },
                    {
                        key: "version",
                        type: "input",
                        label: "Product version"
                    }
                ],
                [
                    {
                        key: "osversion",
                        type: "select",
                        options: MINOS,
                        label: "OS Version",
                        rules: [
                            { required: true }
                        ]
                    },
                    {
                        key: "sysarch",
                        type: "select",
                        options: SYSARCH,
                        label: "System architecture",
                        rules: [
                            { required: true }
                        ]
                    }
                ],
                {
                    key: "log",
                    type:"file",
                    label: "VSI$SUPPORT.COM Script Output",
                    extraLabel: <a href="https://vmssoftware.com/products/vsisupport" target="_blank">More info...</a>
                },
                {
                    key: "file",
                    type: "file",
                    label: "Attachments"
                }
            ],
            initialValues: COMMUNITY_ORGS.includes(orgAlias) ? { product: "Community Requests" } : {}
        },
        grants: {
            width: 320,
            title: userEmail && values.orgAlias ? "Edit Grant" : "Issue a grant",
            fields: [
                {
                    key: "email",
                    type: "input",
                    label: "E-Mail",
                    rules: [
                        { required: true },
                        { pattern: /^[a-zA-Z0-9._%+-]{1,64}@(?:[a-zA-Z0-9-]{1,63}\.){1,125}[a-zA-Z]{2,63}$/, message: "Invalid e-mail." }
                    ],
                    disabled: userEmail
                },
                {
                    key: "orgAlias",
                    type: "select",
                    label: "Organization",
                    options: isAdmin ? aliases : [{ key: orgAlias, title: `[${orgAlias}] ${defaultOrgName}` }],
                    search: true,
                    disabled: editMode,
                    loading: loading,
                    rules: [
                        { required: true }
                    ]
                },
                {
                    key: "grantName",
                    type: "select",
                    label: "Grant Name",
                    options: isAdmin ? GRANTS : filter(GRANTS, { visible: true }),
                    rules: [
                        { required: true }
                    ]
                },
                {
                    key: "setExpiration",
                    type: "checkbox",
                    label: "Set Expiration date",
                    dependencies: "expiryDate"
                },
                state.values.setExpiration
                ? {
                    key: "expiryDate",
                    type: "date",
                    label: "Expiration date",
                    rules: [
                        { required: state.values.setExpiration }
                    ]
                }
                : {}
            ],
            initialValues: {
                email: userEmail,
                orgAlias: null,
                grantName: "partner",
                setExpiration: false
                // expiryDate: null
            }
        },
        contracts: {
            width: 320,
            title: editMode ? "Edit Contract" : "Apply Contract",
            fields: [
                {
                    key: "orgAlias",
                    label: "Organization alias",
                    type: "select",
                    options: aliases,
                    search: true,
                    disabled: !aliases.length || editMode || alias,
                    loading: loading,
                    rules: [
                        { required: true }
                    ]
                },
                {
                    key: "contractName",
                    type: "select",
                    label: "Contract name",
                    options: filter(contracts, c => c.active),
                    loading: loading,
                    disabled: !contracts.length || editMode,
                    rules: [
                        { required: true }
                    ]
                },
                {
                    key: "ecokits",
                    label: "Rights to Ecokits and updates",
                    type: "checkbox"
                },
                {
                    key: "rToMinorRel",
                    label: "Rights to minor releases",
                    type: "checkbox"
                },
                {
                    key: "endDate",
                    type: "date",
                    label: "Expiration date",
                    rules: [
                        { required: true }
                    ]
                }
            ],
            initialValues: {
                orgAlias: alias
            }
        },
        packages: {
            width: 520,
            title: editMode ? "Update Package Info" : "Register Package",
            fields: [
                {
                    key: "name",
                    type: "input",
                    label: "Package Name",
                    max: 39,
                    uppercase: "true",
                    rules: [
                        {
                            pattern: /^[A-Za-z0-9_$\-.]*$/,
                            message: "The package name can contain only uppercase letters, numbers, and underscores and has a maximum length of 39 characters."
                        },
                        {
                            required: true
                        }
                    ]
                },
                [
                    {
                        key: "version",
                        type: "input",
                        label: "Package Version",
                        max: 30,
                        uppercase: "true",
                        rules: [
                            {
                                pattern: /^([A-Z]\d+)\.(\d+)\-([A-Z0-9])+(\-[A-Z0-9]+)*$/,
                                message: 'Specify the version number with up to 15 characters as Vx.x-e-e, where V is an uppercase Latin letter, x is a combination of numbers, and e is a combination of uppercase alphanumeric characters (last e is optional). Ex: V1.0-1A.'
                            },
                            {
                                required: true
                            }
                        ]
                    },
                    {
                        key: "sysarch",
                        type:"select",
                        label: "System Architecture",
                        options: SYSARCH,
                        mode: "multiple",
                        rules: [
                            {
                                required: true
                            }
                        ]
                    },
                    {
                        key: "minos",
                        type:"select",
                        label: "OS Version",
                        options: filter(MINOS, os => !os.searchOnly),
                        mode: "multiple",
                        rules: [
                            {
                                required: true
                            }
                        ]
                    },
                ],
                [
                    {
                        key: "style",
                        type:"select",
                        label: "Kit Style",
                        options: KITSTYLE,
                        rules: [
                            {
                                required: true
                            }
                        ]
                    },
                    {
                        key: "swtype",
                        type: "select",
                        label: "Kit Type",
                        options: filter(SWTYPES, type => !type.searchOnly),
                        dependencies: "ecoRating",
                        rules: [
                            {
                                required: true
                            }
                        ]
                    },
                    {
                        key: "ecoRating",
                        type: "select",
                        label: "ECO Kit Rating",
                        options: ECO_RATING,
                        disabled: state.values.swtype !== "e",
                        rules: [
                            {
                                required: state.values.swtype === "e"
                            }
                        ]
                    }
                ],
                editMode
                ? {}
                : {
                    key: "contract",
                    type:"select",
                    label: "Package Permissions",
                    options: contracts,
                    mode: "multiple",
                    search: true
                },
                {
                    key: "product",
                    type: "select",
                    options: products,
                    label: "product name",
                    search: true
                },
                {
                    key: "files",
                    label: "File",
                    multiple: true,
                    maxWidth: "350px",
                    fields: [
                        {
                            key: "file",
                            type: "file",
                            dependencies: "md5",
                            label: "File",
                            rules: [
                                {
                                    key: "name",
                                    pattern: /^[A-Za-z0-9_\-.$]*$/,
                                    message: "The file name can contain only Latin letters, numbers, dots, $, hyphens and underscores."
                                },
                                {
                                    required: true
                                }
                            ]
                        },
                        {
                            key: "md5",
                            type: "input",
                            label: "MD5 checksum",
                            placeholder: "Enter MD5 checksum (for .ZIP and .ZIPEXE files)",
                            disabled: (values) => {
                                if (has(values, "file") && values.file) {
                                    const re = /[^\\]*\.(\w+)$/,
                                    matched = values.file['name'].match(re);
                                    if (matched && ["zip", "zipexe"].includes(toLower(matched[1]))) {
                                        return false
                                    }
                                }
                                return true
                            },
                            rules: [
                                {
                                    pattern: /^[a-f0-9]{32}$/gi,
                                    message: "Not MD5 hash."
                                },
                                {
                                    required: (values) => {
                                        if (has(values, "file") && values.file) {
                                            const re = /[^\\]*\.(\w+)$/,
                                            matched = values.file['name'].match(re);
                                            if (matched && ["zip", "zipexe"].includes(toLower(matched[1]))) {
                                                return true
                                            }
                                        }
                                        return false
                                    }
                                }
                            ]
                        }
                    ]
                },
                {
                    key: "description",
                    type:"text",
                    label: "Description",
                    rows: 5,
                    rules: [
                        {
                            required: true
                        }
                    ]
                },
                {
                    key: "restart",
                    type: "checkbox",
                    label: "Restart required"
                },
                {
                    key: "releaseDate",
                    type: "date",
                    label: "Release date"
                }
            ],
            initialValues: {
                restart: false
            }
        },
        restrictions: {
            width: 360,
            title: "Package permissions",
            okButtonText: "Save",
            fields: [
                {
                    key: "contracts",
                    multiple: true,
                    type: "select",
                    label: "Contract",
                    options: filter(contracts, contract => findIndex(state.values.contracts, { name: contract.title }) < 0),
                    loading: loading,
                    disabled: !contracts.length,
                    maxWidth: "200px",
                    search: true
                },
                {
                    key: "ecokits",
                    label: "Rights to Ecokits and updates",
                    type: "checkbox",
                    disabled: state.values && Array.isArray(state.values.contracts) && !state.values.contracts.length
                },
                {
                    key: "rToMinorRel",
                    label: "Rights to minor releases",
                    type: "checkbox",
                    disabled: state.values && Array.isArray(state.values.contracts) && !state.values.contracts.length
                }
            ]
        },
        prereq: {
            width: 360,
            okButtonText: "Save",
            title: "Package Prerequisites",
            fields: [
                {
                    key: "prereq",
                    multiple: true,
                    label: "Prerequisite",
                    type: "select",
                    search: true,
                    options: filter(packages, p => p.verified == 1 && p.key != state.values.key && findIndex(state.values.prereq, pr => pr && pr.packageNumber == p.key) < 0 ).map(d => ({ key: d.key, title: d.name })),
                    loading: loading,
                    disabled: !packages.length,
                    maxWidth: "210px",
                    render: (d) => ({
                            packageNumber: Number(d),
                            name: get(find(packages, { key: d }), "name", ""),
                            version: get(find(packages, { key: d }), "version", null)
                        })
                }
            ]
        },
        pkg_mass_update: {
            width: 520,
            title: "Update Packages Info",
            fields: [
                {
                    key: "sysarch",
                    type:"select",
                    label: "System Architecture",
                    options: SYSARCH,
                    mode: "multiple"
                },
                {
                    key: "osVersions",
                    type:"select",
                    label: "OS Versions",
                    options: Array.isArray(state.values.sysarch) ? MINOS.filter(os => os.sysarch.filter(sysarch => state.values.sysarch.includes(sysarch)).length && !os.searchOnly) : [],
                    mode: "multiple",
                    disabled: !(Array.isArray(state.values.sysarch) && state.values.sysarch.length),
                    rules: [
                        {
                            required: Array.isArray(state.values.sysarch) && state.values.sysarch.length > 0
                        }
                    ]
                },
                [{
                    key: "swtype",
                    type: "select",
                    label: "Kit Type",
                    allowClear: true,
                    options: filter(SWTYPES, type => !type.searchOnly),
                    dependencies: "ecoRating"
                },
                {
                    key: "ecoRating",
                    type: "select",
                    label: "ECO Kit Rating",
                    options: ECO_RATING,
                    disabled: state.values.swtype !== "e",
                    rules: [
                        {
                            required: state.values.swtype === "e"
                        }
                    ]
                }],
                {
                    key: "contracts",
                    type:"select",
                    label: "Package Permissions",
                    options: contracts,
                    mode: "multiple",
                    search: true,
                    disabled: state.values.clearContracts
                },
                [{
                    key: "ecokits",
                    label: "Rights to Ecokits and updates",
                    type: "select",
                    options: YESNO,
                    allowClear: true,
                    disabled: !(Array.isArray(state.values.contracts) && state.values.contracts.length),
                    rules: [
                        {
                            required: Array.isArray(state.values.contracts) && state.values.contracts.length > 0
                        }
                    ]
                },
                {
                    key: "rToMinorRel",
                    label: "Rights to minor releases",
                    type: "select",
                    options: YESNO,
                    allowClear: true,
                    disabled: !(Array.isArray(state.values.contracts) && state.values.contracts.length),
                    rules: [
                        {
                            required: Array.isArray(state.values.contracts) && state.values.contracts.length > 0
                        }
                    ]
                }],
                {
                    key: "clearContracts",
                    type: "checkbox",
                    label: "Clear permissions",
                    dependencies: ["contracts", "ecokits", "rToMinorRel"]
                }
            ]
        },
        contract: {
            width: 320,
            title: "Create Contract",
            fields: [
                {
                    key: "name",
                    type: "input",
                    label: "Contract name",
                    rules: [
                        { required: true },
                        { pattern: /^[a-zA-Z0-9-]*$/, message: "Invalid Contract name." }
                    ]
                },
                {
                    key: "description",
                    type: "text",
                    label: "description",
                }
            ]
        },
        password: {
            width: 320,
            title: "Change password",
            fields: [
                {
                    key: "oldpassword",
                    type: "password",
                    label: "Old password",
                    rules: [
                        { required: true }
                    ]
                },
                {
                    key: "newpassword",
                    type: "password",
                    label: "New password",
                    rules: [
                        { required: true },
                        {
                            pattern: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[$#@!%*&])[0-9a-zA-Z$#@!%*&]{8,50}$/,
                            message: "Password must be between 8 and 50 characters and contain Latin letters, numbers, at least one special character ($#@!%*&) and at least one capital Latin letter."
                        }
                    ]
                }
            ]
        },
        orgs_report: {
            tab: "organisations",
            title: "Organizations report",
            fields: [
                {
                    key: "org_name",
                    type: "input",
                    label: "Organization name",
                    max: 60,
                    placeholder: "The account's company name or its part."
                },
                {
                    key: "org_alias",
                    type: "input",
                    label: "Organization alias",
                    max: 3,
                    placeholder: "The organization's alias or its part."
                },
                {
                    key: "email",
                    type: "input",
                    label: "Contact email",
                    max: 60,
                    placeholder: "Сontact email address or its part."
                },
                {
                    key: "phone",
                    type: "input",
                    label: "Contact phone",
                    max: 30,
                    placeholder: "Сontact phone number or its part."
                },
                {
                    key: "updated_by",
                    type: "input",
                    label: "Last updated by",
                    max: 60,
                    placeholder: "Name of the person who was the last to update the profile data or its part."
                }
            ]
        },
        users_report: {
            tab: "users",
            title: "Users report",
            fields: [
                {
                    key: "email",
                    type: "input",
                    label: "email",
                    max: 60,
                    placeholder: "User's email address or its part."
                },
                {
                    key: "full_name",
                    type: "input",
                    label: "User full name",
                    max: 60,
                    placeholder: "User's name or its part."
                },
                {
                    key: "grant",
                    type: "select",
                    mode: "multiple",
                    label: "Grant Name",
                    options: GRANTS,
                    placeholder: "The name of the user grant."
                },
                {
                    key: "grant_expired",
                    type: "select",
                    label: "Grant expired",
                    options: YESNO,
                    placeholder: "User's grant expiration status."
                },
                {
                    key: "org_name",
                    type: "input",
                    label: "Organization name",
                    max: 60,
                    placeholder: "The account's company name or its part."
                },
                {
                    key: "org_alias",
                    type: "input",
                    label: "Organization alias",
                    max: 3,
                    placeholder: "The organization's alias or its part."
                },
                {
                    key: "disabled",
                    type: "select",
                    label: "Account disabled",
                    options: YESNO,
                    placeholder: "User's account status (enabled/disabled)."
                },
                {
                    key: "last_updated_by",
                    type: "input",
                    label: "Last updated by",
                    max: 60,
                    placeholder: "Name of the person who was the last to update the profile data or its part."
                }
            ]
        },
        packages_report: {
            tab: "packages",
            title: "Packages report",
            fields: [
                {
                    key: "name",
                    type: "input",
                    label: "Package name",
                    max: 60,
                    placeholder: "Name or a part of the name of the package being added as it is displayed in the UI."
                },
                {
                    key: "minos",
                    type: "select",
                    options: MINOS,
                    label: "OS Version",
                    mode: "multiple",
                    placeholder: "OS version that the package can be installed on."
                },
                {
                    key: "sysarch",
                    type: "select",
                    options: SYSARCH,
                    label: "System architecture",
                    mode: "multiple",
                    placeholder: "System architecture that the package is compatible with."
                },
                {
                    key: "registered_by",
                    label: "registered by",
                    type: "input",
                    placeholder: "Email of the user who registered a package.",
                    "max": 60
                },
                {
                    key: "registered_datetime_from",
                    label: "registered date from",
                    type: "date",
                    placeholder: "Start date of the period which the package was registered in."
                },
                {
                    key: "registered_datetime_to",
                    label: "registered date to",
                    type: "date",
                    placeholder: "End date of the period which the package was registered in."
                },
                {
                    key: "verified",
                    label: "verified",
                    type: "select",
                    options: YESNO,
                    placeholder: "Package verification status (verified or not)."
                },
                {
                    key: "verified_by",
                    label: "verified by",
                    type: "input",
                    placeholder: "Email of the user who verified a package.",
                    "max": 60
                },
                {
                    key: "verified_datetime_from",
                    label: "release date from",
                    type: "date",
                    placeholder: "Start date of the period which the package was verified in."
                },
                {
                    key: "verified_datetime_to",
                    label: "release date to",
                    type: "date",
                    placeholder: "End date of the period which the package was verified in."
                },
                {
                    key: "updated_by",
                    label: "updated by",
                    type: "input",
                    placeholder: "Email of the user who updated a package.",
                    "max": 60
                }
            ]
        },
        download_audit_report: {
            page: "audit",
            tab: "downloads",
            title: "Packages Download Audit report",
            fields: [
                {
                    key: "package_name",
                    type: "input",
                    label: "Package name",
                    max: 60,
                    placeholder: "Name or a part of the name of the package being added as it is displayed in the UI."
                },
                {
                    key: "version",
                    type: "input",
                    label: "Package version",
                    placeholder: "Version or a part of the version of the package being downloaded."
                },
                {
                    key: "sysarch",
                    type: "select",
                    options: SYSARCH,
                    label: "System architecture",
                    mode: "multiple",
                    placeholder: "System architecture that the package is compatible with."
                },
                {
                    key: "full_name",
                    type: "input",
                    label: "User full name",
                    max: 60,
                    placeholder: "User's name or its part."
                },
                {
                    key: "org_name",
                    type: "input",
                    label: "Organization name",
                    max: 60,
                    placeholder: "The account's company name or its part."
                },
                {
                    key: "downloaded_datetime_from",
                    label: "Downloaded date from",
                    type: "date",
                    placeholder: "Start date of the period which the package was downloaded."
                },
                {
                    key: "downloaded_datetime_to",
                    label: "Downloaded date to",
                    type: "date",
                    placeholder: "End date of the period which the package was downloaded."
                }
            ]
        },
        packages_audit_report: {
            page: "audit",
            tab: "packages",
            title: "Packages Audit report",
            fields: [
                {
                    key: "package_name",
                    type: "input",
                    label: "Package name",
                    max: 60,
                    placeholder: "Name or a part of the name of the package being added as it is displayed in the UI."
                },
                {
                    key: "action",
                    type: "select",
                    label: "Status",
                    options: PACKAGE_STATUS,
                    mode: "multiple",
                    placeholder: "Status of package update."
                },
                {
                    key: "updated_by",
                    type: "input",
                    label: "Updated by",
                    max: 60,
                    placeholder: "Name of the person who was the last to update the profile data or its part."
                },
                {
                    key: "updated_datetime_from",
                    label: "Updated date from",
                    type: "date",
                    placeholder: "Start date of the period which the package was updated."
                },
                {
                    key: "updated_datetime_to",
                    label: "Updated date to",
                    type: "date",
                    placeholder: "End date of the period which the package was updated."
                }
            ]
        },
        users_audit_report: {
            page: "audit",
            tab: "users",
            title: "Users Audit report",
            fields: [
                {
                    key: "email",
                    type: "input",
                    label: "email",
                    max: 60,
                    placeholder: "User's email address or its part."
                },
                {
                    key: "full_name",
                    type: "input",
                    label: "User full name",
                    max: 60,
                    placeholder: "User's name or its part."
                },
                {
                    key: "org_name",
                    type: "input",
                    label: "Organization name",
                    max: 60,
                    placeholder: "The account's company name or its part."
                },
                {
                    key: "action",
                    type: "select",
                    options: USER_STATUS,
                    mode: "multiple",
                    label: "Action",
                    placeholder: "The account's company name or its part."
                },
                {
                    key: "updated_by",
                    type: "input",
                    label: "Last updated by",
                    max: 60,
                    placeholder: "Name of the person who was the last to update the profile data or its part."
                },
                {
                    key: "updated_datetime_from",
                    label: "Updated date from",
                    type: "date",
                    placeholder: "Start date of the period which the user account was updated."
                },
                {
                    key: "updated_datetime_to",
                    label: "Updated date to",
                    type: "date",
                    placeholder: "End date of the period which the user account was updated."
                }
            ]
        },
        grants_audit_report: {
            page: "audit",
            tab: "grants",
            title: "User Grants Audit report",
            fields: [
                {
                    key: "email",
                    type: "input",
                    label: "email",
                    max: 60,
                    placeholder: "User's email address or its part."
                },
                {
                    key: "full_name",
                    type: "input",
                    label: "User full name",
                    max: 60,
                    placeholder: "User's name or its part."
                },
                {
                    key: "org_name",
                    type: "input",
                    label: "Organization name",
                    max: 60,
                    placeholder: "The account's company name or its part."
                },
                {
                    key: "grant",
                    type: "select",
                    options: GRANTS,
                    mode: "multiple",
                    label: "Grant name",
                    placeholder: "The user's grant name."
                },
                {
                    key: "status",
                    type: "select",
                    options: GRANT_STATUS,
                    label: "Grant status",
                    placeholder: "The user's grant status."
                },
                {
                    key: "updated_by",
                    type: "input",
                    label: "Last updated by",
                    max: 60,
                    placeholder: "Name of the person who was the last to update the profile data or its part."
                },
                {
                    key: "updated_datetime_from",
                    label: "Updated date from",
                    type: "date",
                    placeholder: "Start date of the period which the user account was updated."
                },
                {
                    key: "updated_datetime_to",
                    label: "Updated date to",
                    type: "date",
                    placeholder: "End date of the period which the user account was updated."
                }
            ]
        },
        impersonations_audit_report: {
            page: "audit",
            tab: "impersonations",
            title: "User Impersonation Audit report",
            fields: [

                {
                    key: "email",
                    type: "input",
                    label: "email",
                    max: 60,
                    placeholder: "User's email address or its part."
                },
                {
                    key: "full_name",
                    type: "input",
                    label: "User full name",
                    max: 60,
                    placeholder: "User's name or its part."
                },
                {
                    key: "org_name",
                    type: "input",
                    label: "Organization name",
                    max: 60,
                    placeholder: "The account's company name or its part."
                },
                {
                    key: "start_date_from",
                    label: "Start date from",
                    type: "date",
                    placeholder: "Start date of the period which the user account was updated."
                },
                {
                    key: "start_date_to",
                    label: "Start date to",
                    type: "date",
                    placeholder: "End date of the period which the user account was updated."
                },
                {
                    key: "end_date_from",
                    label: "End date from",
                    type: "date",
                    placeholder: "Start date of the period which the user account was updated."
                },
                {
                    key: "end_date_to",
                    label: "End date to",
                    type: "date",
                    placeholder: "End date of the period which the user account was updated."
                }
            ]
        },
        orgs_contracts_audit_report: {
            page: "audit",
            tab: "contracts",
            title: "Organizations Contracts Audit report",
            fields: [
                {
                    key: "contract_name",
                    type: "input",
                    label: "Contract name",
                    max: 60,
                    placeholder: "Contract's name or its part."
                },
                {
                    key: "org_name",
                    type: "input",
                    label: "Organization name",
                    max: 60,
                    placeholder: "The account's company name or its part."
                },
                {
                    key: "status",
                    type: "select",
                    options: CONTRACT_STATUS,
                    label: "Contract status",
                    placeholder: "The contract's grant status."
                },
                {
                    key: "updated_by",
                    type: "input",
                    label: "Last updated by",
                    max: 60,
                    placeholder: "Name of the person who was the last to update the profile data or its part."
                },
                {
                    key: "updated_datetime_from",
                    label: "Updated date from",
                    type: "date",
                    placeholder: "Start date of the period which the organization's contract was updated."
                },
                {
                    key: "updated_datetime_to",
                    label: "Updated date to",
                    type: "date",
                    placeholder: "End date of the period which the organization's contract was updated."
                }
            ]
        },
        orgs_audit_report: {
            page: "audit",
            tab: "orgs",
            title: "Organizations audit report",
            fields: [
                {
                    key: "org_name",
                    type: "input",
                    label: "Organization name",
                    max: 60,
                    placeholder: "The organization's name or its part."
                },
                {
                    key: "org_alias",
                    type: "input",
                    label: "Organization alias",
                    max: 3,
                    placeholder: "The organization's alias or its part."
                },
                {
                    key: "action",
                    type: "select",
                    options: ORG_STATUS,
                    mode: "multiple",
                    label: "Organization status",
                    placeholder: "The organization's grant status."
                },
                {
                    key: "updated_by",
                    type: "input",
                    label: "Last updated by",
                    max: 60,
                    placeholder: "Name of the person who was the last to update the profile data or its part."
                },
                {
                    key: "updated_datetime_from",
                    label: "Updated date from",
                    type: "date",
                    placeholder: "Start date of the period which the organization was updated."
                },
                {
                    key: "updated_datetime_to",
                    label: "Updated date to",
                    type: "date",
                    placeholder: "End date of the period which the organization was updated."
                }
            ]
        },
        orgs_contracts_report: {
            tab: "contracts",
            title: "Organizations Contracts report",
            fields: [
                {
                    key: "contract_name",
                    type: "input",
                    label: "Contract name",
                    max: 60,
                    placeholder: "Contract's name or its part."
                },
                {
                    key: "org_name",
                    type: "input",
                    label: "Organization name",
                    max: 60,
                    placeholder: "The account's company name or its part."
                },
                {
                    key: "email",
                    type: "input",
                    label: "Registered or updated by",
                    max: 60,
                    placeholder: "Name of the person who registered or was the last to update the organization contract, or its part."
                },
                {
                    key: "end_date_from",
                    label: "Expiration date from",
                    type: "date",
                    placeholder: "Start date of the period which the organization's contract was updated."
                },
                {
                    key: "end_date_to",
                    label: "Expiration date to",
                    type: "date",
                    placeholder: "End date of the period which the organization's contract was updated."
                }
            ]
        },
        licenses: {
            tab: "licenses",
            title: editMode ? "Edit license info" : "Add License",
            fields: [
                {
                    key: "orgAlias",
                    type: "select",
                    label: "Organization name",
                    options: aliases,
                    search: true,
                    disabled: !aliases.length || editMode || alias,
                    loading: loading,
                    rules: [
                        { required: true }
                    ]
                },
                editMode
                ? {
                    key: "fileName",
                    type: "input",
                    disabled: true,
                    label: "File",
                    rules: [
                        {
                            required: true
                        }
                    ]
                }
                : {
                    key: "file",
                    type: "file",
                    label: "File",
                    rules: [
                        {
                            required: true
                        }
                    ]
                },
                {
                    key: "expiryDate",
                    type: "date",
                    label: "Expiration date",
                    rules: [
                        {
                            required: true
                        }
                    ]
                },
                {
                    key: "notes",
                    type: "text",
                    label: "notes"
                }
            ],
            initialValues: {
                orgAlias: alias
            }
        }
    };

    if (Object.keys(formFields).indexOf(entity) > -1 && typeof entity === "string" && entity.endsWith("report")) {
        formFields[entity].okButtonText = "Save";
        formFields[entity].fields.unshift({
            key: "report_format",
            type: "select",
            label: "Report format",
            options: REPORT_FORMATS,
            rules: [
                { required: true }
            ]
        });
    };

    const validateValue = (field, value, key, values) => {
        // when the field editing check if value corresponds to its pattern or not empty if required
        if (typeof field === "string") {
            const { fields } = formFields[entity];
            field = find(flatten(fields), { key: field });
            if (key === "file" && has(field, "max") && value) {
                if (countFileSizeLeft(field.max) - value.size < 0) {
                    return "Maximum files size excedeed"
                };
            }
        }
        if (key && has(field, "fields")) {
            field = find(field.fields, { key: key })
        }

        if (field.rules && Array.isArray(field.rules)) {
            let errors = filter(field.rules.map(rule => {
                if (value) {
                    if (rule.pattern && !rule.pattern.test(rule.key ? value[rule.key] : value)) {
                        return rule.message;
                    }
                } else if (typeof rule.required === "function" ? rule.required(values) : rule.required) {
                    return field.label + " is required."
                };
                return false;
            }), e => e !== false);
            if (errors.length) return errors[0]
        };
        return false;
    };

    const onValueChange = (key, value) => {
        const { fields } = formFields[entity];
        const field = find(flatten(fields), { key: key });

        if (field) {
            let newValue = { ...state.values, [key]: value };
            let error = { [key]: validateValue(field, value) };
            let dependencies = typeof field.dependencies == "string" ? field.dependencies.split() : field.dependencies;
            if (dependencies && Array.isArray(dependencies)) {
                dependencies.map(dependency => {
                    let dependetField = find(flatten(fields), { key: dependency });
                    if (dependetField) {
                        if (dependetField.mode == "multiple") {
                            newValue[dependency] = [];
                        } else {
                            newValue[dependency] = null;
                        }
                        error[dependency] = false;
                    }
                })
            }
            if (entity === "users" && editMode && key === "orgAlias") {
                // set grant from selected organization
                let userGrant = find(state.values.grants, { orgAlias: value })
                newValue["grant"] = userGrant && userGrant.grantName ? userGrant.grantName : "partner";
            }
            setState(state => ({
                ...state,
                values: newValue,
                errors: {
                    ...state.errors,
                    ...error
                }
            }));
            let updatedHasChanges = !compareArrays(Object.keys(values).sort(), Object.keys(newValue).sort()) || !compareArrays(Object.values(values).sort(), Object.values(newValue).sort());
            if (hasChanges !== updatedHasChanges) dispatch(hasChanged(updatedHasChanges));
        };
    };

    const validateValues = (fields, values) => {
        // check if all required fields are filled before submit
        let errors = clone(state.errors);
        let fieldsToValidate = flatten(fields);
        return new Promise((resolve, reject) => {
            fieldsToValidate.map(field => {
                const { key, rules, label, multiple } = field;
                let requiredRule = find(rules, rule => has(rule, "required")), isRequired;
                if (requiredRule) {
                    let value = key == "files" || key == "log"
                        ? values[key] && !multiple ? values[key].name : values[key]
                        : values[key];
                    if (typeof requiredRule.required === "function") {
                        isRequired = requiredRule.required(values);
                    } else {
                        isRequired = requiredRule.required;
                    }
                    if (isRequired) {
                        if (value === null || value === undefined || (typeof value == "string" && !value.trim().length) || (Array.isArray(value) && !value.length)) {
                            if (!errors[key]) {
                                errors = Object.assign({}, errors, { [key]: `${label} is required.` })
                            };
                        };
                    }
                };
            });
            if (filter(Object.values(errors), o => o !== false).length) {
                return reject(errors);
            } else {
                return resolve();
            };
        });
    };

    const onSave = () => {
        validateValues(formFields[entity].fields, state.values)
            .then(() => {
                let body = {};
                let changedValues = {};
                Object.keys(state.values).map(key => {
                    if (state.values[key] !== values[key]) {
                        changedValues[key] = state.values[key];
                    }
                });
                if (entity === "users") {
                    const { email, name, orgAlias, grant, expiry } = state.values;
                    body = JSON.stringify({
                        email: email,
                        full_name: name,
                        org_alias: orgAlias,
                        grant: editMode ? undefined :  grant,
                        expiry_date: expiry
                    });
                }
                else if (entity === "organisations") {
                    const { alias, name, addr, phone, email, notes } = state.values;
                    body = JSON.stringify({
                        name: name,
                        alias: alias,
                        addr: addr,
                        phone: phone,
                        email: email,
                        notes: notes
                    });
                }
                else if (entity === "grants") {
                    const { email, orgAlias, grantName, expiryDate } = state.values;
                    body = JSON.stringify({
                        email: email,
                        org_alias: orgAlias,
                        grant: grantName,
                        expiry_date: noneToNull(expiryDate)
                    });
                }
                else if (entity === "contracts") {
                    const { orgAlias, contractName, endDate, ecokits, rToMinorRel } = state.values;
                    body = JSON.stringify({
                        org_alias: orgAlias,
                        contract_name: contractName,
                        end_date: formatDate(endDate, "YYYY-MM-DD"),
                        ecokits: Number(ecokits) || 0,
                        r_to_minor_rel: Number(rToMinorRel) || 0
                    });
                }
                else if (entity === "packages") {
                    const { name, version, sysarch, minos, style, swtype, description, restart, files, ecoRating, contract, releaseDate, product } = changedValues;
                    body.data = {
                        name: name?.toUpperCase() || values.name,
                        description: description,
                        version: version?.toUpperCase(),
                        minos: minos,
                        sysarch: sysarch,
                        style: style?.toUpperCase(),
                        swtype: ecoRating ? state.values.swtype?.toUpperCase() : swtype?.toUpperCase(),
                        restart_required: typeof restart == "boolean" ? Number(restart) : restart,
                        eco_rating: ecoRating ? Number(ecoRating) : undefined,
                        release_date: releaseDate ? formatDate(releaseDate, "YYYY-MM-DD") : releaseDate,
                        product_number: product ? Number(product) : product
                    };
                    if (editMode) {
                        body.data["package_number"] = values.key;
                        if (files) body.data["file_ids_to_delete"] = filter(values.files, file => findIndex(files, { key: file.key }) < 0).map(o => o.key);
                    } else {
                        body.data["prereq"] = null;
                        body.data["contract"] = contract;
                    }
                    body.files = filter(files, f => !has(f, "key"));
                }
                else if (entity === "restrictions") {
                    const { key, contracts, ecokits, rToMinorRel } = state.values;
                    body = JSON.stringify({
                        package_number: key,
                        contracts: contracts.map(c => c.name),
                        ecokits: Number(ecokits),
                        r_to_minor_rel: Number(rToMinorRel)
                    });
                }
                else if (entity === "prereq") {
                    const { key, prereq } = state.values;
                    body = JSON.stringify({
                        package_number: key,
                        prereq: prereq.map((pr, idx) => ({
                            package_number: Number(pr.packageNumber),
                            order: ++idx,
                            name: pr.name,
                            version: pr.version
                        }))
                    });
                }
                else if (entity === "contract") {
                    const { name, description } = state.values;
                    body = JSON.stringify({
                        name: name,
                        description: description
                    });
                } else if (entity === "password") {
                    const { oldpassword, newpassword } = state.values;
                    body = JSON.stringify({
                        email: email,
                        password: oldpassword,
                        newpassword: newpassword
                    })
                } else if (entity === "users_report") {
                    const { grant_expired, disabled } = state.values;
                    body = {
                        "grant_expired": grant_expired === undefined ? grant_expired : Number(grant_expired),
                        "disabled": disabled === undefined ? disabled : Number(disabled)
                    };
                } else if (entity === "packages_report") {
                    const { verified } = state.values;
                    body = {
                        "verified": verified === undefined ? verified : Number(verified)
                    };
                } else if (entity === "licenses") {
                    const { id, expiryDate, notes } = state.values;
                    body = {
                        id: id,
                        expiryDate: formatDate(expiryDate, "YYYY-MM-DD"),
                        notes: notes
                    };
                } else if (entity === "pkg_mass_update") {
                    const { sysarch, osVersions, swtype, ecoRating, contracts, ecokits, rToMinorRel, clearContracts } = changedValues;
                    body = {
                        package_ids: Array.isArray(values.key) ? values.key.map(k => Number(k)) : values.key,
                        os_versions: Array.isArray(osVersions) && !osVersions.length ? undefined : osVersions,
                        sysarch: Array.isArray(sysarch) && !sysarch.length ? undefined : sysarch,
                        swtype: swtype?.toUpperCase(),
                        eco_rating: swtype === "e" && ecoRating ? Number(ecoRating) : undefined,
                        contracts: Array.isArray(contracts) && !contracts.length && !clearContracts ? undefined : contracts,
                        ecokits: ecokits === undefined ? ecokits : Number(ecokits),
                        r_to_minor_rel: rToMinorRel === undefined ? rToMinorRel : Number(rToMinorRel)
                    };
                };

                if (entity === "packages") {
                    dispatch(registerPackage({ data: body.data, files: body.files, edit: editMode }));
                } else if (entity === "pkg_mass_update") {
                    dispatch(updatePackages(body));
                } else if (entity === "licenses") {
                    editMode ? dispatch(editOrgLicense(body)) : dispatch(addOrgLicense(state.values));
                } else if (typeof entity === "string" && entity.endsWith("report")) {
                    dispatch(getReport({ page: formFields[entity].page, tab: formFields[entity].tab, values: { ...state.values, ...body }}));
                } else {
                    dispatch(addItem({ entity: entity, values: body }));
                };
                dispatch(showForm(false));
            })
            .catch(errors => {
                setState(state => ({ ...state, errors: errors }));
            });
    };

    return(
        Object.keys(formFields).indexOf(entity) > -1 && entity
            ? <ActionForm
                title={formFields[entity].title}
                fields={formFields[entity].fields}
                values={state.values}
                errors={state.errors}
                onClose={() => dispatch(showForm(false))}
                onValueChange={onValueChange}
                onSave={onSave}
                hasChanges={hasChanges}
                width={formFields[entity].width || 500}
                okButtonText={formFields[entity].okButtonText}
                validateValue={validateValue}
                validateValues={validateValues}
            />
            : null
    );
};

export { ActionFormContainer };